import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useMoanaPaye } from '../contexts/MoanaPayeContext';
import {
  LayoutDashboard, Building2, Users, FileText, Settings, LogOut,
  ChevronLeft, ChevronRight, Menu, X, Calculator, CalendarDays, ClipboardList, FileCheck, BarChart3, ShieldCheck, User
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moana-paye-module/artifacts/oaca6hlg_LOGO%20GROUPE%20AERH%20POLYNESIE%20COULEUR%20%28%C3%A0%20utiliser%20sur%20fond%20clair%29%20PNG%20%282%29.png";

const NAV_FULL = [
  { label: 'Tableau de bord', path: '/moana-paye/dashboard', icon: LayoutDashboard },
  { label: 'Entreprises', path: '/moana-paye/entreprises', icon: Building2 },
  { label: 'Salariés', path: '/moana-paye/salaries', icon: Users },
  { label: 'Absences & Congés', path: '/moana-paye/absences', icon: CalendarDays },
  { label: 'Variables de paie', path: '/moana-paye/variables', icon: ClipboardList },
  { label: 'Déclarations', path: '/moana-paye/declarations', icon: FileCheck },
  { label: 'Reporting', path: '/moana-paye/reporting', icon: BarChart3 },
  { label: 'Simulation', path: '/moana-paye/simulation', icon: Calculator },
  { label: 'Paramètres', path: '/moana-paye/parametres', icon: Settings },
  { label: 'Administration', path: '/moana-paye/administration', icon: ShieldCheck },
];

const NAV_SALARIE = [
  { label: 'Mon espace', path: '/moana-paye/mon-espace', icon: User },
];

function getNav(profil) {
  if (profil === 'salarie') return NAV_SALARIE;
  if (profil === 'lecture') return NAV_FULL.filter(n => !['administration'].includes(n.path.split('/').pop()));
  return NAV_FULL;
}

export default function MoanaPayeLayout() {
  const { user, loading, logout, can, loadPermissions, formule, mpFetch } = useMoanaPaye();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Load permissions on mount
  useEffect(() => {
    if (user && user.profil !== 'salarie') {
      mpFetch('/api/moana-paye/entreprises').then(ents => {
        if (ents?.length) loadPermissions(ents[0].id);
      }).catch(() => {});
    }
  }, [user]);

  if (loading) return <div className="flex items-center justify-center h-screen bg-[#1a1f2e]"><div className="text-white text-lg">Chargement...</div></div>;
  if (!user) return <Navigate to="/moana-paye/login" replace />;

  const NAV = getNav(user.profil);

  // Filter nav based on permissions
  const filteredNav = NAV.filter(item => {
    const page = item.path.split('/').pop();
    if (page === 'variables' && !can('voir_variables')) return false;
    if (page === 'simulation' && !can('simulation')) return false;
    if (page === 'declarations' && !can('voir_declarations')) return false;
    return true;
  });

  const Sidebar = ({ mobile }) => (
    <aside className={`${mobile ? 'w-64' : collapsed ? 'w-16' : 'w-60'} bg-[#1a2332] flex flex-col h-full transition-all duration-200`}>
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0 p-1">
            <img src={LOGO_URL} alt="AERH" className="w-full h-full object-contain" />
          </div>
          {(!collapsed || mobile) && <span className="text-white font-semibold text-sm truncate">Moana Paye</span>}
        </div>
        {(!collapsed || mobile) && formule && (
          <div className="mt-2 text-[10px] font-medium tracking-wider uppercase px-2 py-1 rounded-md bg-white/5 text-slate-400 text-center" data-testid="mp-formule-badge">
            {formule === 'delegation' ? 'Délégation' : formule === 'semi' ? 'Semi-délégation' : 'Autonomie'}
          </div>
        )}
      </div>
      <nav className="flex-1 py-2">
        {filteredNav.map(item => {
          const active = location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => mobile && setMobileOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg text-sm transition-colors ${
                active ? 'bg-[#2D5A5A] text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`mp-nav-${item.path.split('/').pop()}`}
            >
              <item.icon className="w-4.5 h-4.5 flex-shrink-0" />
              {(!collapsed || mobile) && <span className="truncate">{item.label}</span>}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10">
        {(!collapsed || mobile) && (
          <div className="text-xs text-slate-500 mb-2 px-2 truncate">{user.email}</div>
        )}
        <button onClick={logout} className="flex items-center gap-2 px-2 py-2 text-slate-400 hover:text-red-400 text-sm w-full rounded-lg hover:bg-white/5 transition-colors">
          <LogOut className="w-4 h-4 flex-shrink-0" />
          {(!collapsed || mobile) && <span>Deconnexion</span>}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-[#0f1520] overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex flex-col relative">
        <Sidebar />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-[#2D5A5A] rounded-full flex items-center justify-center text-white hover:bg-[#E8A020] transition-colors z-10"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="relative w-64 h-full">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-[#1a2332] border-b border-white/10 flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden text-slate-400 hover:text-white">
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-white/80 text-sm font-medium hidden sm:block">
              {NAV.find(n => location.pathname.startsWith(n.path))?.label || 'Moana Paye'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#E8A020] font-medium bg-[#E8A020]/10 px-2 py-1 rounded">
              {user.profil?.replace('_', ' ')}
            </span>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
